import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Venue } from "./VenueService";

// GraphQL queries
const RUNNING_MESSAGE_LIST_QUERY = gql`
  query RunningMessageList(
    $filter: RunningMessageFilter
    $params: PaginationAndSorting
  ) {
    runningMessageList(filter: $filter, params: $params) {
      code
      data {
        id
        venueId
        messageSourceType
        name
        messageText
        scrollSpeed
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        importance
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RUNNING_MESSAGE_GET_QUERY = gql`
  query RunningMessageGet($id: ID) {
    runningMessageGet(id: $id) {
      code
      data {
        id
        venueId
        messageSourceType
        name
        messageText
        scrollSpeed
        backgroundColor
        textColor
        fontSize
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RUNNING_MESSAGE_GET_MANY_QUERY = gql`
  query RunningMessageGetMany($ids: [ID]) {
    runningMessageGetMany(ids: $ids) {
      code
      data {
        id
        venueId
        messageSourceType
        name
        messageText
        scrollSpeed
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        importance
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const RUNNING_MESSAGE_CREATE_MUTATION = gql`
  mutation RunningMessageCreate($params: RunningMessageInput) {
    runningMessageCreate(params: $params) {
      code
      data {
        id
        venueId
        messageSourceType
        name
        messageText
        scrollSpeed
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        importance
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RUNNING_MESSAGE_UPDATE_MUTATION = gql`
  mutation RunningMessageUpdate(
    $where: RunningMessageWhereInput
    $params: RunningMessageInput
  ) {
    runningMessageUpdate(where: $where, params: $params) {
      code
      data {
        id
        venueId
        messageSourceType
        name
        messageText
        scrollSpeed
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        importance
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RUNNING_MESSAGE_REMOVE_MUTATION = gql`
  mutation RunningMessageRemove($where: RunningMessageWhereInput) {
    runningMessageRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RUNNING_MESSAGE_REMOVE_MANY_MUTATION = gql`
  mutation RunningMessageRemoveMany($where: [RunningMessageWhereInput]) {
    runningMessageRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface RunningMessage {
  id: string;
  venueId?: string;
  messageSourceType?: string;
  name?: string;
  messageText?: string;
  scrollSpeed?: number;
  startDatetime?: string;
  endDatetime?: string;
  startTime?: string;
  endTime?: string;
  recurrencePattern?: string;
  recurrenceDays?: string;
  importance?: number;
  status?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  venue?: Venue;
}

export interface RunningMessageFilter {
  id?: string[] | null | undefined;
  venueId?: string[] | null | undefined;
  messageSourceType?: string | null | undefined;
  messageText?: string | null | undefined;
  searchKeyword?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface RunningMessageInput {
  venueId?: string | null | undefined;
  messageSourceType?: string | null | undefined;
  name?: string | null | undefined;
  messageText?: string | null | undefined;
  scrollSpeed?: number | null | undefined;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  recurrencePattern?: string | null | undefined;
  recurrenceDays?: string | null | undefined;
  importance?: number | null | undefined;
  status?: number | null | undefined;
}

export interface RunningMessageWhereInput {
  id: string;
}

/**
 * Running Message service that handles all running message-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class RunningMessageService {
  /**
   * Get a list of running messages with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: RunningMessageFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(RUNNING_MESSAGE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single running message by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(RUNNING_MESSAGE_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple running messages by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RUNNING_MESSAGE_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new running message
   */
  static async create(params: RunningMessageInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RUNNING_MESSAGE_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing running message
   */
  static async update(
    id: string,
    params: RunningMessageInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RUNNING_MESSAGE_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a running message
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RUNNING_MESSAGE_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple running messages
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      RUNNING_MESSAGE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
